"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { ProgramJourney } from "@/components/ui/organisms/program-journey";
import { announcePieceSource, onAnalyticsSessionReset, track } from "@/lib/analytics";
import {
  getProgram,
  type ProgramDetail,
  type ProgramMilestone,
} from "@/lib/api/api-client";
import { useAuthedView } from "@/lib/learn/use-authed-view";
import { learnExercisePath } from "@/lib/learn/learn-routes";
import type { ProgramState } from "@/lib/learning-journey/program-state";
import { useProgramProgress } from "@/lib/learning-journey/use-program-progress";
import { libraryPiecePath } from "@/lib/library/library-routes";
import { getLevelStyle } from "./level-styles";

/**
 * L'identité d'un jalon, telle qu'elle survit à une resynchronisation de
 * contenu.
 *
 * `milestone.id` ne convient pas : le constructeur de parcours vide puis
 * reconstruit les jalons à chaque fichier VPF corrigé, donc l'UUID change sans
 * que rien n'ait bougé pour personne, et toutes les courbes de franchissement
 * repartiraient de zéro. Ce que le fichier écrit, lui, tient : la clé du
 * morceau pour un jalon de morceau, le rang déclaré pour une étape, qui n'a
 * pas d'autre identifiant d'auteur. Même exigence que pour `programId`, qui
 * porte déjà le `vpfId` et non l'UUID.
 */
function milestoneKey(milestone: ProgramMilestone): string {
  return milestone.kind === "piece"
    ? `piece:${milestone.pieceKey}`
    : `step:${milestone.position}`;
}

/**
 * Les jalons déjà franchis quand on a vu ce parcours pour la première fois de
 * la session, par identifiant de parcours.
 *
 * Au niveau du module, et c'est tout l'enjeu : cette page se démonte dès qu'on
 * part jouer, et c'est justement au retour qu'un jalon a changé d'état. Amorcé
 * dans un `useRef`, l'état réamorcerait à chaque visite exactement ce qu'il
 * doit détecter, et `program_milestone_reached` ne partirait jamais. Amorcé
 * nulle part, il réémettrait tout l'acquis à chaque ouverture de la page.
 */
const seededMilestones = new Map<string, Set<string>>();

/*
  Une nouvelle session anonyme oublie l'amorçage de la précédente.

  Sans ça, changer de compte sans recharger la page comparait la progression du
  compte suivant à l'amorçage du précédent : tout ce que le nouveau venu avait
  déjà franchi et que l'ancien n'avait pas partait en salve de faux
  franchissements — d'autant plus fournie que le compte précédent était neuf.
  Un poste partagé, ou simplement une déconnexion, suffisait.
*/
onAnalyticsSessionReset(() => {
  seededMilestones.clear();
});

/**
 * La progression tenue a-t-elle bien été calculée sur **ce** parcours-ci ?
 *
 * `useProgramProgress` recalcule dans un effet : au rendu où la relecture
 * signée remplace la vue anonyme, l'état rendu est encore celui de la vue
 * précédente. Amorcer là-dessus, ou en publier le compte, décrit un parcours
 * qui n'est plus à l'écran.
 *
 * La longueur ne suffit pas à s'en garder — deux vues du même parcours ont le
 * même nombre de jalons dès que la vue anonyme n'est pas verrouillée, et le
 * décalage passe alors tout entier inaperçu. `deriveProgramState` reporte en
 * revanche les objets `milestone` eux-mêmes dans son résultat : comparer les
 * identités **prouve** la filiation au lieu de la supposer, et le rendu
 * intermédiaire est écarté à coup sûr.
 */
function derivedFrom(progress: ProgramState, program: ProgramDetail): boolean {
  return (
    progress.milestones.length === program.milestones.length &&
    progress.milestones.every(
      (status, index) => status.milestone === program.milestones[index],
    )
  );
}

/**
 * Le parcours arrive du serveur, déjà dans le HTML. Ce composant ne reste
 * client que pour la navigation et pour l'état de progression, qui vit dans le
 * navigateur.
 */
export function ProgramDetailPage({
  program: served,
}: {
  program: ProgramDetail;
}) {
  const router = useRouter();
  // Le serveur a rendu la vue anonyme. Si quelqu'un est connecté, il a peut
  // être le droit d'ouvrir ce parcours : on redemande, signé.
  const { view: program, settled } = useAuthedView(served, () =>
    getProgram(served.vpfId),
  );
  const style = getLevelStyle(program.level);

  /*
    Une seconde lecture de la progression, pour la mesure seule.

    `ProgramJourney` a déjà la sienne, mais la lui reprendre demanderait de
    remonter son état dans ses props — c'est-à-dire de faire dépendre la forme
    d'un composant présentationnel de ce qu'on veut compter. Le hook relit
    quelques clés de `localStorage` dans un effet : le coût est celui d'une
    boucle sur les jalons, et il achète la seule propriété qui distingue une
    découverte d'un retour.
  */
  const progress = useProgramProgress(program);
  const reportedProgramId = useRef<string | null>(null);

  useEffect(() => {
    /*
      Deux attentes avant de compter.

      `settled` : le HTML servi est celui d'un visiteur anonyme, donc `locked`
      y vaut vrai pour toute personne connectée tant que la relecture signée
      n'a pas abouti. Émettre avant ferait dire à la mesure que tout le monde
      se heurte au mur, y compris ceux qui ne le voient jamais — et `locked`
      est précisément la propriété pour laquelle cet événement existe.

      `progress` : nul le temps que le stockage soit lu, ce qui ferait dire
      « zéro jalon franchi » à tout le monde, et retardataire d'un rendu sur le
      parcours — `milestonesDone` se lirait alors sur la vue anonyme, qui n'a
      aucun jalon là où la vue signée en a douze.
    */
    if (
      !settled ||
      progress === null ||
      !derivedFrom(progress, program) ||
      reportedProgramId.current === program.vpfId
    ) {
      return;
    }

    reportedProgramId.current = program.vpfId;

    track({
      name: "program_opened",
      properties: {
        programId: program.vpfId,
        level: program.level,
        access: program.access,
        locked: program.locked,
        // Un parcours fermé arrive sans ses jalons : les compteurs valent
        // alors zéro, ce qui est exact — il n'y a rien à franchir tant qu'on
        // ne peut pas l'ouvrir.
        milestoneCount: program.milestones.length,
        milestonesDone: progress.doneCount,
      },
    });
  }, [program, progress, settled]);

  /*
    Le franchissement lui-même, qui n'existait nulle part.

    `program_opened.milestonesDone` n'en est qu'une photo prise à l'arrivée :
    on savait combien de jalons étaient faits, jamais quand ni lequel. Or la
    progression est la seule question que /learn pose, et un parcours mené à
    son terme ne laissait pas la moindre trace alors que `deriveProgramState`
    calcule déjà `isComplete` avant de le jeter.
  */
  useEffect(() => {
    // Mêmes deux attentes que `program_opened`, pour les mêmes raisons. Un
    // parcours fermé arrive de surcroît sans ses jalons : il n'y a alors rien
    // à amorcer, et amorcer sur du vide ferait passer l'ouverture de l'accès
    // pour une salve de franchissements.
    if (!settled || progress === null || program.milestones.length === 0) {
      return;
    }

    /*
      La progression retarde d'un rendu sur le parcours : `useProgramProgress`
      la recalcule dans un effet, donc un cran après que la relecture signée a
      remplacé la vue anonyme. Amorcer sur ce rendu-là ferait passer des jalons
      acquis de longue date pour autant de franchissements.

      La comparaison des longueurs ne l'attrapait qu'à moitié : elle ne voit
      que le cas où la vue anonyme est verrouillée, donc vide. Deux vues du même
      parcours ouvert ont exactement le même compte de jalons, et l'état
      intermédiaire y passait entier. C'est l'identité des jalons qui tranche.
    */
    if (!derivedFrom(progress, program)) {
      return;
    }

    const seeded = seededMilestones.get(program.vpfId);

    if (seeded === undefined) {
      seededMilestones.set(
        program.vpfId,
        new Set(
          progress.milestones
            .filter((status) => status.state === "done")
            .map((status) => milestoneKey(status.milestone)),
        ),
      );

      return;
    }

    const crossed = progress.milestones
      .map((status, index) => ({ status, index }))
      .filter(
        ({ status }) =>
          status.state === "done" && !seeded.has(milestoneKey(status.milestone)),
      );

    crossed.forEach(({ status, index }, rank) => {
      seeded.add(milestoneKey(status.milestone));

      track({
        name: "program_milestone_reached",
        properties: {
          programId: program.vpfId,
          milestoneId: milestoneKey(status.milestone),
          // Le rang dans le parcours entier, jalons cachés compris : c'est le
          // même dénominateur que `program_opened.milestoneCount`, sans quoi
          // les deux événements décriraient deux parcours différents.
          index,
          total: progress.milestones.length,
          // Porté par le dernier jalon du lot et par lui seul : deux jalons
          // franchis pendant la même absence annonceraient sinon deux fois la
          // même fin de parcours.
          completed: progress.isComplete && rank === crossed.length - 1,
        },
      });
    });
  }, [program, progress, settled]);

  return (
    <ProgramJourney
      program={program}
      color={style.color}
      hue={style.hue}
      onBack={() => router.push("/learn")}
      onOpenExercise={(exercise, origin) => {
        track({
          name: "program_exercise_opened",
          properties: {
            programId: program.vpfId,
            /*
              L'UUID, et non le `vefId` qui adresse la page.

              Ce palier portait le `vefId`, le palier suivant
              (`exercise_started`, émis par le studio) porte l'UUID : les deux
              ne se rejoignaient sur aucune clé, donc l'entonnoir de /learn
              était tout simplement incalculable. C'est l'UUID qui tranche, et
              pas l'inverse, parce que `ExerciseDetail` — ce que le studio a en
              main quand il charge — n'expose pas de `vefId` du tout : il vit
              dans `parameters`, côté serveur. Un exercice généré n'en a même
              aucun.

              L'adresse, elle, garde le `vefId` juste en dessous : elle est
              faite pour être lue, pas pour être jointe.
            */
            exerciseId: exercise.id,
            origin,
            category: exercise.category ?? undefined,
            difficulty: exercise.difficulty,
            status: exercise.progress?.status ?? "unknown",
          },
        });

        router.push(learnExercisePath(program.vpfId, exercise.vefId ?? exercise.id));
      }}
      onOpenPiece={(piece) => {
        /*
          On annonce la provenance, on n'émet pas l'ouverture.

          La branche `source: "program"` n'avait jamais eu d'émetteur : le
          morceau de récompense d'un parcours sortait de la mesure au moment
          précis où il devient intéressant. Mais l'émettre ici compterait un
          clic, alors que `piece_opened` décrit désormais le chargement réel
          dans le studio — et le morceau serait compté deux fois, une fois ici
          et une fois à l'ouverture, sous deux provenances différentes.

          Le studio, lui, ne peut pas deviner : la récompense s'ouvre depuis sa
          fiche de catalogue comme n'importe quel morceau de catalogue. Cette
          surface est la seule à savoir d'où part le geste, elle le dit donc, et
          le chargement le reprend.
        */
        announcePieceSource(piece.id, "program");

        router.push(libraryPiecePath(piece));
      }}
    />
  );
}
